import {getCsrfToken} from "@lib/functions";

/**
 * HTTP APIリクエストのオプション設定
 */
export interface ApiRequestOptions {
    /** CSRFトークンを含めるかどうか（デフォルト: true） */
    includeCsrf?: boolean;
    /** 追加のヘッダー */
    headers?: Record<string, string>;
    /** リクエストタイムアウト（ミリ秒） */
    timeout?: number;
}

/**
 * FormDataを使ったHTTP APIリクエストクラス
 * ジェネリクス対応で任意のレスポンス型に対応
 * ユニットテスト可能な設計
 */
export class ApiFormData {
    private formData: globalThis.FormData;
    private readonly defaultHeaders: Record<string, string>;

    constructor() {
        this.formData = new globalThis.FormData();
        this.defaultHeaders = {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
        };
    }

    /**
     * フィールドを追加
     */
    append(key: string, value: string | number | boolean): this {
        this.formData.append(key, String(value));
        return this;
    }

    /**
     * ファイルを追加
     */
    appendFile(key: string, file: File): this {
        this.formData.append(key, file);
        return this;
    }

    /**
     * 複数フィールドを一度に追加
     */
    appendFields(fields: Record<string, string | number | boolean>): this {
        Object.entries(fields).forEach(([key, value]) => {
            this.append(key, value);
        });
        return this;
    }

    /**
     * CSRFトークンを追加
     */
    appendCsrfToken(): this {
        const token = getCsrfToken();
        if (token) {
            this.formData.append('_csrfToken', token);
        }
        return this;
    }

    /**
     * GETリクエストを実行
     */
    async get<T>(
        url: string,
        options: ApiRequestOptions = {}
    ): Promise<T> {
        const {headers = {}, timeout = 30000} = options;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    ...this.defaultHeaders,
                    ...headers
                },
                credentials: 'same-origin',
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json() as T;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error(`Request timeout after ${timeout}ms`);
            }
            throw error;
        }
    }

    /**
     * POSTリクエストを実行
     */
    async post<T>(
        url: string,
        options: ApiRequestOptions = {}
    ): Promise<T> {
        const {
            includeCsrf = true,
            headers = {},
            timeout = 30000
        } = options;

        // CSRFトークンを自動追加
        if (includeCsrf) {
            this.appendCsrfToken();
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeout);

        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    ...this.defaultHeaders,
                    ...headers
                },
                credentials: 'same-origin',
                body: this.formData as BodyInit,
                signal: controller.signal
            });

            clearTimeout(timeoutId);

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            return await response.json() as T;
        } catch (error) {
            clearTimeout(timeoutId);
            if (error instanceof Error && error.name === 'AbortError') {
                throw new Error(`Request timeout after ${timeout}ms`);
            }
            throw error;
        }
    }

    /**
     * FormDataの中身を取得（デバッグ用）
     */
    getFormData(): globalThis.FormData {
        return this.formData;
    }

    /**
     * FormDataの中身をオブジェクトとして取得（テスト用）
     */
    getFormDataAsObject(): Record<string, string> {
        const obj: Record<string, string> = {};
        this.formData.forEach((value, key) => {
            obj[key] = String(value);
        });
        return obj;
    }

    /**
     * FormDataをリセット
     */
    reset(): this {
        this.formData = new globalThis.FormData();
        return this;
    }

    /**
     * ファクトリーメソッド: インスタンス作成
     */
    static create(): ApiFormData {
        return new ApiFormData();
    }

    /**
     * ファクトリーメソッド: フィールド付きインスタンス作成
     */
    static createWithFields(fields: Record<string, string | number | boolean>): ApiFormData {
        return new ApiFormData().appendFields(fields);
    }
}

/**
 * APIレスポンスのエラーハンドリング用ユーティリティ
 */
export class ApiError extends Error {
    constructor(
        message: string,
        public readonly statusCode?: number,
        public readonly response?: any
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

/**
 * APIレスポンス検証ユーティリティ
 */
export function validateApiResponse<T>(response: any, validator?: (data: any) => boolean): T {
    if (response?.STATUS === 'ERROR') {
        throw new ApiError(response.MESSAGE || 'API Error', undefined, response);
    }

    if (validator && !validator(response)) {
        throw new ApiError('Invalid response format', undefined, response);
    }

    return response as T;
}
